import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { workspaceAPI, spaceAPI, listAPI, taskAPI } from '../lib/api';

const WorkspaceContext = createContext(null);

// LocalStorage keys
const STORAGE_KEYS = {
    LAST_WORKSPACE_ID: 'digitask_last_workspace_id',
};

export const useWorkspace = () => {
    const context = useContext(WorkspaceContext);
    if (!context) {
        throw new Error('useWorkspace must be used within a WorkspaceProvider');
    }
    return context;
};

export const WorkspaceProvider = ({ children }) => {
    const [workspaces, setWorkspaces] = useState([]);
    const [currentWorkspace, setCurrentWorkspace] = useState(null);
    const [spaces, setSpaces] = useState([]);
    const [currentSpace, setCurrentSpace] = useState(null);
    const [lists, setLists] = useState([]);
    const [currentList, setCurrentList] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [members, setMembers] = useState([]);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [currentUserSpaceIds, setCurrentUserSpaceIds] = useState([]);  // For client role space restrictions
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [storageStats, setStorageStats] = useState(null);

    const [workflows] = useState([
        "Not Started",
        "Active",
        "Review",
        "Done",
    ]);

    const fetchWorkspaces = useCallback(async () => {
        try {
            if(workspaces.length > 0) return workspaces;
            const response = await workspaceAPI.getAll();
            setWorkspaces(response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch workspaces:', error);
            return [];
        }
    }, []);

    const selectWorkspace = useCallback(async (workspace) => {
        setCurrentWorkspace(workspace);
        setCurrentSpace(null);
        setCurrentList(null);
        setTasks([]);
        
        // Save to localStorage
        if (workspace) {
            localStorage.setItem(STORAGE_KEYS.LAST_WORKSPACE_ID, workspace.id);
        }
        
        if (workspace) {
            try {
                // First get members to determine user role and space access
                const [spacesRes, membersRes] = await Promise.all([
                    spaceAPI.getByWorkspace(workspace.id),
                    workspaceAPI.getMembers(workspace.id),
                ]);
                
                // Find current user role and space access
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const userMember = membersRes.data.find(m => m.user_email === currentUser.email);
                const userRole = userMember?.role || null;
                const userSpaceIds = userMember?.space_ids || [];
                
                setCurrentUserRole(userRole);
                setCurrentUserSpaceIds(userSpaceIds);
                
                // Filter spaces for non-owner/admin roles - only show spaces they have access to
                const isSpaceRestricted = userRole && !['owner', 'admin'].includes(userRole) && userSpaceIds.length > 0;
                let filteredSpaces = spacesRes.data;
                if (isSpaceRestricted) {
                    filteredSpaces = spacesRes.data.filter(space => userSpaceIds.includes(space.id));
                }

                setSpaces(filteredSpaces);
                setMembers(membersRes.data);

                // Fetch stats with space filtering for restricted roles
                const statsSpaceIds = isSpaceRestricted ? userSpaceIds : null;
                const [statsRes, storageRes] = await Promise.all([
                    workspaceAPI.getStats(workspace.id, statsSpaceIds),
                    workspaceAPI.getStorageStats(workspace.id),
                ]);
                setStats(statsRes.data);
                setStorageStats(storageRes.data);
            } catch (error) {
                console.error('Failed to fetch workspace data:', error);
            }
        } else {
            setSpaces([]);
            setStats(null);
            setMembers([]);
            setCurrentUserRole(null);
            setCurrentUserSpaceIds([]);
        }
    }, []);

    // Auto-select last workspace on initial load
    useEffect(() => {
        const initWorkspace = async () => {
            if (initialLoadDone) return;
            
            try {
                const response = await workspaceAPI.getAll();
                const allWorkspaces = response.data || [];
                setWorkspaces(allWorkspaces);
                
                if (allWorkspaces.length > 0) {
                    // Check for saved workspace
                    const savedWorkspaceId = localStorage.getItem(STORAGE_KEYS.LAST_WORKSPACE_ID);
                    let workspaceToSelect = null;
                    
                    if (savedWorkspaceId) {
                        workspaceToSelect = allWorkspaces.find(w => w.id === savedWorkspaceId);
                    }
                    
                    // Fallback to first workspace if saved one not found
                    if (!workspaceToSelect) {
                        workspaceToSelect = allWorkspaces[0];
                    }
                    
                    if (workspaceToSelect && !currentWorkspace) {
                        await selectWorkspace(workspaceToSelect);
                    }
                }
            } catch (error) {
                console.error('Failed to initialize workspace:', error);
            } finally {
                setInitialLoadDone(true);
            }
        };
        
        initWorkspace();
    }, [initialLoadDone, selectWorkspace, currentWorkspace]);

    const selectSpace = useCallback(async (space) => {
        setCurrentSpace(space);
        setCurrentList(null);
        
        if (space) {
            try {
                const [listsRes, tasksRes] = await Promise.all([
                    listAPI.getBySpace(space.id),
                    taskAPI.getBySpace(space.id)
                ]);
                setLists(listsRes.data);
                setTasks(tasksRes.data);
                console.log('Lists', lists);
            } catch (error) {
                console.error('Failed to fetch space data:', error);
            }
        } else {
            setLists([]);
            setTasks([]);
        }
    }, []);

    const selectList = useCallback(async (list) => {
        setCurrentList(list);
        
        if (list) {
            try {
                const response = await taskAPI.getByList(list.id);
                setTasks(response.data);
            } catch (error) {
                console.error('Failed to fetch list tasks:', error);
            }
        }
    }, []);

    const refreshTasks = useCallback(async () => {
        if (currentList) {
            const response = await taskAPI.getByList(currentList.id);
            setTasks(response.data);
        } else if (currentSpace) {
            const response = await taskAPI.getBySpace(currentSpace.id);
            setTasks(response.data);
        }
    }, [currentList, currentSpace]);

    const addTask = useCallback((task) => {
        setTasks(prev => [...prev, task]);
    }, []);


    const refreshStats = useCallback(async () => {
        if (currentWorkspace) {
            try {
                const response = await workspaceAPI.getStats(currentWorkspace.id);
                setStats(response.data);
            } catch (error) {
                console.error('Failed to refresh stats:', error);
            }
        }
    }, [currentWorkspace]);

    const refreshMembers = useCallback(async () => {
        if (currentWorkspace) {
            try {
                const response = await workspaceAPI.getMembers(currentWorkspace.id);
                setMembers(response.data);
            } catch (error) {
                console.error('Failed to refresh members:', error);
            }
        }
    }, [currentWorkspace]);

    const refreshStorage = useCallback(async (workspaceId) => {
        const id = workspaceId || currentWorkspace?.id;
        if (!id) return;
        try {
            const response = await workspaceAPI.getStorageStats(id);
            setStorageStats(response.data);
        } catch (error) {
            console.error('Failed to fetch storage stats:', error);
        }
    }, [currentWorkspace]);

    const getNotStartedWorkflow = () => {
        lists.filter(l => l.group === 'Not Started').map(l => l.name) || [];
    };
    
    const getActiveWorkflow = () => {
        lists.filter(l => l.group === 'Active').map(l => l.name) || [];
    };
    const getReviewWorkflow = () => {
        lists.filter(l => l.group === 'Review').map(l => l.name) || [];
    };
    const getDoneWorkflow = () => {
        lists.filter(l => l.group === 'Active').map(l => l.name) || [];
    };

    const getFirstNotStartedWorkflow = ()=> {
        getNotStartedWorkflow()[0] || '-';
    };
    
    const getFirstActiveWorkflow = () => {
        getActiveWorkflow()[0] || '-';
    };
    const getFirstReviewWorkflow = () => {
        getReviewWorkflow()[0] || '-';
    };
    const getFirstDoneWorkflow = () => {
        getDoneWorkflow()[0] || '-';
    };

    const getUserNamebyId = (userId) => {
        const user = members.find(member => member.id === userId);
        return user ? user.name : 'Unknown User';
    };



    const value = {
        workspaces,
        currentWorkspace,
        spaces,
        currentSpace,
        lists,
        currentList,
        tasks,
        loading,
        stats,
        members,
        currentUserRole,
        currentUserSpaceIds,
        workflows,
        // Helper flags for role-based access
        isClientGuest: currentUserRole === 'client' || currentUserRole === 'guest',
        isReadOnly: currentUserRole === 'client' || currentUserRole === 'guest',
        canEditTasks: currentUserRole !== 'client' && currentUserRole !== 'guest',
        canManageSpace: currentUserRole === 'owner' || currentUserRole === 'admin',
        canManageSettings: currentUserRole === 'owner' || currentUserRole === 'admin',
        setLoading,
        fetchWorkspaces,
        selectWorkspace,
        selectSpace,
        selectList,
        refreshTasks,
        addTask,
        storageStats,
        refreshStorage,
        refreshStats,
        refreshMembers,
        setSpaces,
        setLists,
        setTasks,
    };

    return (
        <WorkspaceContext.Provider value={value}>
            {children}
        </WorkspaceContext.Provider>
    );
};

export default WorkspaceContext;
